import { Link } from "react-router";
import { IoClose } from "react-icons/io5";
import { normalizeRiskLevel, getRiskLevelPriority, PROJECT_STATUS } from "../constants/project";
import { getRiskTailwindClasses } from "../styles/designSystem";

const widgetFont = { fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' };

export const MapPopup = ({ projectData, riskFilter, onClose }: { projectData: ProjectInterface, riskFilter?: string, onClose: () => void }) => {

  // Items that count toward the popup's risk badges/tasks: never completed, and
  // restricted to the active risk filter (mirrors the pin/marker filtering).
  const getVisibleItems = () => {
    const planItems = (projectData.items || []).map((item: any) => ({ ...item, _source: 'plan' as const }));
    const actionItems = (projectData.actionItems || []).map((item: any) => ({ ...item, _source: 'action' as const }));
    const normalizedFilter = riskFilter && riskFilter !== 'all'
      ? normalizeRiskLevel(riskFilter).toLowerCase()
      : null;

    return [...planItems, ...actionItems].filter((item: any) => {
      if (item.status === PROJECT_STATUS.COMPLETED) return false;
      if (normalizedFilter && normalizeRiskLevel(item.risks).toLowerCase() !== normalizedFilter) return false;
      return true;
    });
  };

  // Calculate project completion based on all items (plan + action)
  const getProjectCompletion = (): number => {
    const allItems = [
      ...(projectData.items || []),
      ...(projectData.actionItems || [])
    ];
    if (allItems.length === 0) {
      if (projectData.status === 'COMPLETED') return 100;
      if (projectData.status === 'IN_PROGRESS') return 50;
      return 0;
    }

    const totalCompletion = allItems.reduce((sum: number, item: any) => {
      return sum + (item.completion_percentage || 0);
    }, 0);

    return totalCompletion / allItems.length;
  };

  const site = projectData.sites?.[0];

  // Get full address - prioritize Google Address formatted_address
  const getFullAddress = (site: any) => {
    if (!site) return '';

    if (site.google_address_data?.formatted_address) {
      return site.google_address_data.formatted_address;
    }

    if (site.address) {
      return site.address;
    }

    if (site.google_address_data) {
      const { city, state, locality, administrative_area_level_1 } = site.google_address_data;

      if (city && state) return `${city}, ${state}`;
      if (city) return city;
      if (locality && administrative_area_level_1) return `${locality}, ${administrative_area_level_1}`;
      if (locality) return locality;
    }

    return site.name || '';
  };

  const displayAddress = getFullAddress(site);

  // Count issues by risk level from both plan items and action items
  const getRiskLevelCounts = () => {
    const counts = { critical: 0, high: 0, medium: 0, low: 0, none: 0 };

    getVisibleItems().forEach((item: any) => {
      const risk = normalizeRiskLevel(item.risks).toLowerCase();
      if (counts.hasOwnProperty(risk)) {
        counts[risk as keyof typeof counts]++;
      } else {
        counts.none++;
      }
    });

    return counts;
  };

  const riskCounts = getRiskLevelCounts();

  // Get top priority tasks sorted by risk level (critical first), up to 8
  const getTopTasks = () => {
    return getVisibleItems()
      .sort((a, b) => {
        const aPriority = getRiskLevelPriority(normalizeRiskLevel(a.risks));
        const bPriority = getRiskLevelPriority(normalizeRiskLevel(b.risks));
        return bPriority - aPriority;
      })
      .slice(0, 8);
  };

  const topTasks = getTopTasks();

  // Generate initials for project avatar
  const getInitials = (name: string) => {
    return name.split(' ').map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
  };

  return (
    <div className="map-popup absolute bg-white w-[380px] z-50 rounded-lg border border-[#eaeaea] shadow-[0_4px_16px_rgba(0,0,0,0.08)] p-5" style={widgetFont}>
      {/* Header */}
      <div className="flex justify-between items-start mb-3">
        <div className="flex gap-3 items-start flex-1 min-w-0">
          <div className="w-10 h-10 rounded-lg bg-[#171717] flex items-center justify-center flex-shrink-0">
            <span className="text-white text-[11px] font-bold">{getInitials(projectData.name)}</span>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-[15px] font-semibold text-[#171717] leading-tight truncate tracking-[-0.01em]">
              {projectData.name}
            </h3>
            <p className="text-[12px] text-[#888] mt-0.5 break-words leading-snug">
              {displayAddress}
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="text-[#888] hover:text-[#444] transition-colors p-1 rounded-md hover:bg-[#fafafa] -mr-1 -mt-1"
        >
          <IoClose size={16} />
        </button>
      </div>

      {/* Risk badges + Completion */}
      <div className="flex items-center gap-1.5 mb-4 flex-wrap">
        {riskCounts.critical > 0 && (
          <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-red-50 border border-red-100">
            <div className="w-1.5 h-1.5 rounded-full bg-red-800" />
            <span className="text-[11px] text-red-800 font-medium">{riskCounts.critical}</span>
          </div>
        )}
        {riskCounts.high > 0 && (
          <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-red-50 border border-red-100">
            <div className="w-1.5 h-1.5 rounded-full bg-red-500" />
            <span className="text-[11px] text-red-600 font-medium">{riskCounts.high}</span>
          </div>
        )}
        {riskCounts.medium > 0 && (
          <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-amber-50 border border-amber-100">
            <div className="w-1.5 h-1.5 rounded-full bg-amber-500" />
            <span className="text-[11px] text-amber-700 font-medium">{riskCounts.medium}</span>
          </div>
        )}
        {riskCounts.low > 0 && (
          <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-emerald-50 border border-emerald-100">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            <span className="text-[11px] text-emerald-700 font-medium">{riskCounts.low}</span>
          </div>
        )}
        {riskCounts.critical === 0 && riskCounts.high === 0 && riskCounts.medium === 0 && riskCounts.low === 0 && (
          <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-[#fafafa] border border-[#eaeaea]">
            <div className="w-1.5 h-1.5 rounded-full bg-[#888]" />
            <span className="text-[11px] text-[#666] font-medium">No issues</span>
          </div>
        )}

        {/* Completion */}
        <div className="flex items-center gap-1.5 px-1.5 py-0.5 rounded-md bg-[#fafafa] border border-[#eaeaea] ml-auto">
          <div className="w-3.5 h-3.5 relative">
            <svg className="w-3.5 h-3.5 transform -rotate-90" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="8" stroke="#eaeaea" strokeWidth="2.5" fill="none" />
              <circle
                cx="12" cy="12" r="8" stroke="#171717" strokeWidth="2.5" fill="none"
                strokeDasharray={`${2 * Math.PI * 8}`}
                strokeDashoffset={`${2 * Math.PI * 8 * (1 - (getProjectCompletion() / 100))}`}
                className="transition-all duration-300"
              />
            </svg>
          </div>
          <span className="text-[11px] text-[#444] font-medium tabular-nums">
            {Math.round(getProjectCompletion())}%
          </span>
        </div>
      </div>

      {/* Top Priority Tasks */}
      <div className="space-y-1 max-h-[280px] overflow-y-auto">
        {topTasks.length === 0 ? (
          <div className="text-center py-4 text-[#888]">
            <div className="text-[13px]">No tasks</div>
          </div>
        ) : (
          <>
            <div className="text-[11px] font-medium text-[#888] uppercase tracking-wider mb-2">
              Top Tasks ({topTasks.length})
            </div>
            {topTasks.map((task: any) => {
              const riskLevel = normalizeRiskLevel(task.risks);
              const riskClasses = getRiskTailwindClasses(riskLevel.toLowerCase());
              const taskLink = task._source === 'action'
                ? `/projects/${projectData.id}?tab=action-items&item=${task.id}`
                : `/projects/${projectData.id}?tab=project-plan-2&item=${task.id}`;

              return (
                <Link
                  key={task.id}
                  to={taskLink}
                  className="block p-2 rounded-md border border-[#eaeaea] hover:bg-[#fafafa] hover:border-[#ddd] transition-all duration-150 cursor-pointer"
                >
                  <div className="flex items-center gap-2">
                    <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${riskClasses.bg}`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-medium text-[#444] truncate">
                        {task.name || task.title}
                      </div>
                    </div>
                    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-md ${riskClasses.bgLight} ${riskClasses.text} flex-shrink-0`}>
                      {riskLevel === 'NONE' ? 'None' : riskLevel.charAt(0) + riskLevel.slice(1).toLowerCase()}
                    </span>
                  </div>
                </Link>
              );
            })}
          </>
        )}
      </div>

      {/* Go to Project */}
      <div className="mt-4 pt-4 border-t border-[#eaeaea]">
        <Link
          to={`/projects/${projectData.id}`}
          className="block w-full text-center px-5 py-2.5 bg-[#171717] hover:bg-[#333] text-white text-[13px] font-medium rounded-lg transition-colors duration-150"
          onClick={onClose}
        >
          Open Project
        </Link>
      </div>
    </div>
  );
};
